import { Panel, Stack } from "rsuite";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { CurrentDeviceProvider } from "../context/CurrentDeviceContext";
import { CurrentIdProvider } from "../context/CurrentIdContext";
import InformationPanel from "./InformationPanel";
import ModernDevicesTable from "./ModernDevicesTable";
import { filterType, menuType } from "./DevicesTable";
import {
  getWorkstations,
  getLinuxList,
  getMobileList,
  getServerList,
  getWindowsList,
  getAppleList,
  getDevice,
} from "../Api";
import { DASHBOARD_REFRESH_MS } from "../util/GlobalConstants";
import dev from "../models/joinDevice.json";
import "./AllDevicesPanel.css";
import "./DashboardSkins.css";

/* Structure kept from showcase skin #078 · KEPLER-07 (diagonal weave +
   angular controls). Colors now derive from the active app theme. */
const MAIN_SKIN = {
  number: 78,
  name: "KEPLER-07",
  className: "holo-shape--angular",
};

export const filterTable = filterType;
export const contextMenuTable = menuType;

const API_BY_FILTER = {
  [filterType.all]: getWorkstations,
  [filterType.linux]: getLinuxList,
  [filterType.mobile]: getMobileList,
  [filterType.server]: getServerList,
  [filterType.windows]: getWindowsList,
  [filterType.apple]: getAppleList,
};

/* Per-device resource telemetry (cpu / ram / disk) feeding the table bars.
   The roster endpoints carry no usage figures, so each device's deep info has
   to be fetched on its own — on a slower cadence than the roster refresh. */
const TELEMETRY_SWEEP_MS = 1000 * 10;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const clampPct = (v) => Math.max(0, Math.min(100, v));

const usagePct = (used, total) =>
  total > 0 ? clampPct((used / total) * 100) : undefined;

/* A metric the device does not report stays undefined so the bar renders as a
   dash rather than a reassuring 0%. */
const toTelemetrySample = (info) => {
  const ramUsed = num(info.ram_used);
  const ramTotal = num(info.ram);
  const diskUsed = num(info.hdused);
  const diskTotal = num(info.hd);
  const hasCpu = info.proc_used !== undefined && info.proc_used !== "";
  return {
    cpu: hasCpu ? clampPct(num(info.proc_used)) : undefined,
    ram: usagePct(ramUsed, ramTotal),
    disk: usagePct(diskUsed, diskTotal),
    titles: {
      cpu: hasCpu ? `CPU ${Math.round(clampPct(num(info.proc_used)))}%` : undefined,
      ram: ramTotal > 0 ? `RAM ${ramUsed} / ${ramTotal} GB` : undefined,
      disk: diskTotal > 0 ? `DISK ${diskUsed} / ${diskTotal} GB` : undefined,
    },
  };
};

const tablePanelVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 180, damping: 24, delay: 0.15 },
  },
};

const infoPanelVariants = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 180, damping: 24, delay: 0.25 },
  },
};

const AllDevicePanel = ({ filteredBy = filterTable.all, contextMenuTable }) => {
  const [t] = useTranslation();
  const [devices, setDevices] = useState([]);
  const [telemetry, setTelemetry] = useState({});
  const [currentDevice, setCurrentDevice] = useState();
  const filterRef = useRef(filteredBy);
  const pendingRequest = useRef(false);
  const devicesRef = useRef([]);
  const sweepPending = useRef(false);

  useEffect(() => {
    filterRef.current = filteredBy;
    setDevices([]);
    setTelemetry({});
    devicesRef.current = [];
    const requestDevices = () => {
      if (pendingRequest.current) return;
      pendingRequest.current = true;
      const apiCall = API_BY_FILTER[filteredBy] || getWorkstations;
      apiCall().then((data) => {
        pendingRequest.current = false;
        if (filterRef.current !== filteredBy) return;
        const list = data != null ? data : [];
        devicesRef.current = list;
        setDevices(list);
      });
    };
    requestDevices();
    const interval = setInterval(requestDevices, DASHBOARD_REFRESH_MS);
    return () => clearInterval(interval);
  }, [filteredBy]);

  /* Telemetry sweep over the whole visible roster. Re-armed once the first
     roster lands so the bars fill straight away instead of after a full
     sweep period. */
  const hasDevices = devices.length > 0;
  useEffect(() => {
    const sweep = () => {
      if (sweepPending.current) return;
      const ids = devicesRef.current.map((d) => d[dev.id]);
      if (ids.length === 0) return;
      const sweptFilter = filterRef.current;
      sweepPending.current = true;
      Promise.all(
        ids.map((id) =>
          getDevice(id).then(
            (info) => ({ id, info }),
            () => ({ id, info: null })
          )
        )
      ).then((results) => {
        sweepPending.current = false;
        if (filterRef.current !== sweptFilter) return;
        setTelemetry((prev) => {
          // Rebuilt from this sweep so devices that left the roster drop out;
          // a device whose call failed keeps its last known sample.
          const next = {};
          results.forEach(({ id, info }) => {
            const sample = info ? toTelemetrySample(info) : prev[id];
            if (sample) next[id] = sample;
          });
          return next;
        });
      });
    };
    const kickoff = setTimeout(sweep, 300);
    const interval = setInterval(sweep, TELEMETRY_SWEEP_MS);
    return () => {
      clearTimeout(kickoff);
      clearInterval(interval);
    };
  }, [hasDevices, filteredBy]);

  return (
    <div className="dashboard-view" key={filteredBy}>
      <Stack spacing={16} alignItems="flex-start" wrap>
        <Stack.Item grow={1} style={{ flexBasis: 0, minWidth: "640px" }}>
          <motion.div
            variants={tablePanelVariants}
            initial="hidden"
            animate="visible"
          >
            <Panel
              header={
                <Stack justifyContent="space-between">
                  <span className="dashboard-title">
                    {t("all-devices-panel." + filteredBy)}
                    <span className="dashboard-badge">
                      #{String(MAIN_SKIN.number).padStart(3, "0")} ·{" "}
                      {MAIN_SKIN.name}
                    </span>
                  </span>
                </Stack>
              }
              className={`content dashboard-card dashboard-holo ${MAIN_SKIN.className}`}
            >
              <CurrentDeviceProvider>
                <CurrentIdProvider>
                  <ModernDevicesTable
                    devices={devices}
                    selectedDevice={currentDevice}
                    onSelect={setCurrentDevice}
                    telemetry={telemetry}
                    contextMenuType={contextMenuTable}
                  />
                </CurrentIdProvider>
              </CurrentDeviceProvider>
            </Panel>
          </motion.div>
        </Stack.Item>
        <Stack.Item style={{ width: "420px", flexShrink: 0 }}>
          <motion.div
            variants={infoPanelVariants}
            initial="hidden"
            animate="visible"
          >
            <InformationPanel
              device={currentDevice}
              deviceData={devices.find((d) => d[dev.id] === currentDevice)}
            />
          </motion.div>
        </Stack.Item>
      </Stack>
    </div>
  );
};

export default AllDevicePanel;
